import linkStyle from "@/lib/components/navigation/Header/navbar/linkStyle.shared";
import {
  Icon,
  NavbarMenuItemType,
  useColorScheme,
  useLanguage,
  useLocales,
} from "@/lib/main";
import { NavbarDropdownDirectionType } from "@/lib/types/navigation/header/navbar/NavbarMenuItem.type";
import { CSSProperties } from "react";
import { useLocation } from "react-router";

type Props = {
  direction: NavbarDropdownDirectionType;
  isHashLinks?: boolean;
  isHovered: boolean;
  navbarMenuItem: NavbarMenuItemType;
};

export default function NavbarMenuItemDropdown({
  direction,
  isHashLinks,
  isHovered,
  navbarMenuItem,
}: Props) {
  const l = useLocales();
  const location = useLocation();
  const { colorScheme } = useColorScheme();
  const { textDirection } = useLanguage();

  const { kind } = navbarMenuItem;

  const hasActiveSubitem = (items: NavbarMenuItemType[]): boolean => {
    const currentLocation = isHashLinks
      ? location.pathname + location.hash
      : location.pathname;

    return items.some((item): boolean => {
      if (item.kind === "link") {
        return item.to === currentLocation;
      }

      if (item.kind === "submenu") {
        return hasActiveSubitem(item.submenu);
      }

      return false;
    });
  };

  const isActive =
    kind === "submenu" && hasActiveSubitem(navbarMenuItem.submenu);

  const dropDownLinkStyle: CSSProperties = {
    ...linkStyle,

    color: isActive ? "var(--color-text-link)" : "var(--color-text-primary)",
    paddingInlineStart: 0,
  };

  const buttonContainerStyle: CSSProperties = {
    position: "relative",
    zIndex: "var(--z-index-header-navbar-menu-item-tab)",

    backgroundColor: isHovered
      ? colorScheme === "dark"
        ? "var(--color-background-frame)"
        : "var(--color-background-canvas)"
      : "transparent",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--spacing-xs)",

    height: direction === "down" ? "var(--navbar-height)" : "3rem",
    border: "none",
    padding:
      direction === "down" ? "0 var(--spacing-md)" : "0 var(--spacing-lg)",
    font: "inherit",
    cursor: "pointer",
    width: "100%",
  };

  const iconContainerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    transform: !isHovered
      ? "rotate(0deg)"
      : textDirection === "rtl"
        ? "rotate(180deg)"
        : "rotate(-180deg)",
    transition: "transform 0.5s ease-in-out",
  };

  if (kind !== "submenu") return null;

  return (
    <button
      style={buttonContainerStyle}
      aria-expanded={isHovered}
      aria-haspopup="menu"
      type="button"
    >
      <span style={dropDownLinkStyle}>{l(navbarMenuItem.title)}</span>
      <div style={iconContainerStyle}>
        <Icon
          name={
            direction === "down"
              ? "chevron-down"
              : textDirection === "rtl"
                ? "chevron-left"
                : "chevron-right"
          }
          size={direction === "down" ? "sm" : "xs"}
        />
      </div>
    </button>
  );
}
